import React, { useEffect, useMemo } from "react";
import { Input, Select, Space } from "antd";
import { useSearchParams } from "react-router-dom";
import { debounce } from "lodash";

type TableFilter =
  | "pattern"
  | "version"
  | "platform"
  | "category"
  | "stability"
  | "framework";

interface TablTableQueryFilterProps {
  filter?: TableFilter[];
}

interface TableQueryFilterParams {
  pattern?: string;
  op?: string;
  version?: string;
  platform?: string;
  category?: string;
  stability?: string;
  framework?: string;
}

export const TableQueryFilter = (props: TablTableQueryFilterProps) => {
  const [searchParams, setSearchParams] = useSearchParams();

  const [filter, setFilter] = React.useState<TableQueryFilterParams>({
    pattern: searchParams.get("pattern") || undefined,
    op: searchParams.get("op") || undefined,
    version: searchParams.get("version") || undefined,
    platform: searchParams.get("platform") || undefined,
    category: searchParams.get("category") || undefined,
    stability: searchParams.get("stability") || undefined,
    framework: searchParams.get("framework") || undefined,
  });

  const debouncedUpdateSearchQuery = useMemo(
    () =>
      debounce(
        (
          currentFilter: TableQueryFilterParams,
          currentSearchParams: URLSearchParams
        ) => {
          const params = new URLSearchParams(currentSearchParams);

          currentFilter.pattern
            ? params.set("pattern", currentFilter.pattern)
            : params.delete("pattern");
          currentFilter.op
            ? params.set("op", currentFilter.op)
            : params.delete("op");
          currentFilter.version
            ? params.set("version", currentFilter.version)
            : params.delete("version");
          currentFilter.platform
            ? params.set("platform", currentFilter.platform)
            : params.delete("platform");
          currentFilter.category
            ? params.set("category", currentFilter.category)
            : params.delete("category");
          currentFilter.stability
            ? params.set("stability", currentFilter.stability)
            : params.delete("stability");
          currentFilter.framework
            ? params.set("framework", currentFilter.framework)
            : params.delete("framework");

          setSearchParams(params);
        },
        400
      ),
    [setSearchParams]
  );

  useEffect(() => {
    debouncedUpdateSearchQuery(filter, searchParams);
    return () => debouncedUpdateSearchQuery.cancel();
  }, [filter, debouncedUpdateSearchQuery, searchParams]);

  return (
    <Space>
      {props.filter?.map((f) => {
        switch (f) {
          case "platform":
            return (
              <Select
                key={f}
                allowClear
                showSearch
                placeholder="Select a platform"
                value={filter.platform}
                onChange={(value) => setFilter({ ...filter, platform: value })}
                optionFilterProp="children"
                style={{ width: 150 }}
                options={[
                  { value: "android", label: "Android" },
                  { value: "ios", label: "iOS" },
                ]}
              />
            );
          case "pattern":
            return (
              <Input
                key={f}
                value={filter.pattern || ""}
                onChange={(e) => setFilter({ ...filter, pattern: e.target.value })}
                placeholder="Search by sdk name or status"
                allowClear
              />
            );
          case "version":
            return (
              <Input
                key={f}
                addonBefore={
                  <Select
                    defaultValue={filter.op || "=="}
                    onChange={(value) => setFilter({ ...filter, op: value })}
                    options={[
                      { value: "==", label: "==" },
                      { value: ">=", label: ">=" },
                      { value: "<=", label: "<=" },
                      { value: ">", label: ">" },
                      { value: "<", label: "<" },
                    ]}
                  />
                }
                onChange={(e) =>
                  setFilter({ ...filter, version: e.target.value })
                }
                value={filter.version || ""}
                placeholder="Version"
              />
            );
          case "category":
            return (
              <Select
                key={f}
                allowClear
                showSearch
                placeholder="Select a category"
                value={filter.category}
                onChange={(value) => setFilter({ ...filter, category: value })}
                optionFilterProp="children"
                style={{ width: 150 }}
                options={[
                  { value: "mediation", label: "Mediation" },
                  { value: "ad_network", label: "Ad Network" },
                  { value: "service", label: "Service" },
                  { value: "other", label: "Other" },
                ]}
              />
            );
          case "stability":
            return (
              <Select
                key={f}
                showSearch
                allowClear
                placeholder="Select a stability"
                value={filter.stability}
                onChange={(value) => setFilter({ ...filter, stability: value })}
                optionFilterProp="children"
                style={{ width: 150 }}
                options={[
                  { value: "stable", label: "Stable" },
                  { value: "beta", label: "Beta" },
                  { value: "alpha", label: "Alpha" },
                  { value: "custom", label: "Custom" },
                ]}
              />
            );
          case "framework":
            return (
              <Select
                key={f}
                showSearch
                allowClear
                placeholder="Select a framework"
                value={filter.framework}
                onChange={(value) => setFilter({ ...filter, framework: value })}
                optionFilterProp="children"
                style={{ width: 150 }}
                options={[
                  { value: "unity", label: "Unity" },
                  { value: "other", label: "Other" },
                ]}
              />
            );
        }
      })}
    </Space>
  );
};
